import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Menu,
  Minus,
  Plus,
  X,
  Maximize,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Brand } from "@/components/layout/Brand";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { cn } from "@/lib/cn";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// pdfjs worker -- bundled locally (via the pdfjs-dist dependency react-pdf
// pulls in) instead of fetched from a CDN, so document load never depends on
// a third-party host being fast/reachable and can't skew from react-pdf's
// bundled pdfjs version. Configured once on module load.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export type Section = {
  id: string;
  title: string;
  page?: number;
};

export type PDFViewerProps = {
  url: string;
  title?: string | null;
  subtitle?: string | null;
  helperText?: string | null;
  modeIcon?: LucideIcon;
  modeAccent?: string;
  showStudyControls?: boolean;
  /** Optional sections shown in the left sidebar (textbook mode). */
  sections?: Section[];
  /** Called when the user clicks a section -- typically opens the summary panel. */
  onSectionClick?: (section: Section) => void;
  /** Slot rendered to the right of the toolbar -- e.g. language switcher. */
  toolbarSlot?: React.ReactNode;
  /**
   * "scroll" (default): continuous vertical list, used by textbook/notes/
   * question-bank. "slide": exactly one page on screen at a time, fit to the
   * full viewport like an actual presentation slide -- used by the slide deck.
   */
  viewMode?: "scroll" | "slide";
  /** Called whenever the current page or the page count changes. Used by the
   *  progress tracker to tell when the reader has reached the end. */
  onPageChange?: (page: number, totalPages: number) => void;
  className?: string;
};

// Zoom is a multiplier over "fit width". Default is native-reader style:
// open the PDF fitted to the reading pane, then let the user zoom in/out.
const MIN_SCALE = 0.65;
const MAX_SCALE = 3.0;
const SCALE_STEP = 0.1;
const DEFAULT_SCALE = 1.0;
const DESKTOP_MAX_PAGE_WIDTH = 940;
const MOBILE_MIN_PAGE_WIDTH = 220;
const PAGE_RENDER_RADIUS = 5;
const DEFAULT_PAGE_ASPECT_RATIO = 1.414;

export function PDFViewer({
  url,
  title,
  subtitle,
  helperText,
  modeIcon: ModeIcon,
  modeAccent,
  showStudyControls = false,
  sections,
  onSectionClick,
  toolbarSlot,
  viewMode = "scroll",
  onPageChange,
  className,
}: PDFViewerProps) {
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [visualScale, setVisualScale] = useState(DEFAULT_SCALE);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  // Per-page aspect ratio (height/width), keyed by page number. Most PDFs are
  // uniform, but relying on a single global value meant one oddly-sized page
  // (or float rounding noise) would mis-size every OTHER page's placeholder,
  // shifting layout and destabilizing the IntersectionObserver's "current
  // page" pick mid-scroll -- which could cascade into the actually-visible
  // pages falling outside the render radius and going blank.
  const [pageAspectRatios, setPageAspectRatios] = useState<Map<number, number>>(new Map());
  const pageHostRef = useRef<HTMLDivElement | null>(null);
  const zoomLayerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());
  const visualScaleRef = useRef(visualScale);
  const zoomRafRef = useRef<number | null>(null);
  const zoomStateTimerRef = useRef<number | null>(null);
  const zoomSettleTimerRef = useRef<number | null>(null);
  const pageJumpTimerRef = useRef<number | null>(null);
  const isZoomingRef = useRef(false);
  const isPageJumpingRef = useRef(false);

  const syncPageFromScroll = useCallback(() => {
    const root = pageHostRef.current;
    if (!root || pageRefs.current.size === 0) return;
    const rootRect = root.getBoundingClientRect();
    const viewportCenter = rootRect.top + root.clientHeight / 2;
    let bestPage = pageNumber;
    let bestDistance = Number.POSITIVE_INFINITY;

    pageRefs.current.forEach((node, page) => {
      const rect = node.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const distance = Math.abs(center - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestPage = page;
      }
    });

    setPageNumber(bestPage);
  }, [pageNumber]);

  useEffect(() => {
    visualScaleRef.current = visualScale;
  }, [visualScale]);

  useEffect(() => {
    return () => {
      if (zoomRafRef.current) window.cancelAnimationFrame(zoomRafRef.current);
      if (zoomStateTimerRef.current) window.clearTimeout(zoomStateTimerRef.current);
      if (zoomSettleTimerRef.current) window.clearTimeout(zoomSettleTimerRef.current);
      if (pageJumpTimerRef.current) window.clearTimeout(pageJumpTimerRef.current);
    };
  }, []);

  const basePageWidth = useMemo(() => {
    if (!containerWidth) return undefined;
    const horizontalPadding =
      containerWidth < 640 ? 16 : containerWidth < 1024 ? 32 : 48;
    const availableWidth = Math.max(MOBILE_MIN_PAGE_WIDTH, containerWidth - horizontalPadding);
    return Math.max(
      MOBILE_MIN_PAGE_WIDTH,
      Math.min(availableWidth, DESKTOP_MAX_PAGE_WIDTH),
    );
  }, [containerWidth]);

  const renderedPageWidth = basePageWidth ? Math.round(basePageWidth * scale) : undefined;

  // Fallback for pages not yet measured -- average of what's known so far,
  // falling back to a generic portrait ratio before anything has loaded.
  const fallbackAspectRatio = useMemo(() => {
    if (pageAspectRatios.size === 0) return DEFAULT_PAGE_ASPECT_RATIO;
    let sum = 0;
    pageAspectRatios.forEach((r) => { sum += r; });
    return sum / pageAspectRatios.size;
  }, [pageAspectRatios]);

  const estimatedHeightForPage = useCallback((page: number) => {
    if (!renderedPageWidth) return undefined;
    const ratio = pageAspectRatios.get(page) ?? fallbackAspectRatio;
    return Math.round(renderedPageWidth * ratio);
  }, [renderedPageWidth, pageAspectRatios, fallbackAspectRatio]);

  // Slide mode: fit the CURRENT page fully inside the host viewport (both
  // width and height, like a real presentation slide) instead of fitting
  // width only and letting height run long. "Contain"-style fit.
  const slideBaseWidth = useMemo(() => {
    if (viewMode !== "slide" || !containerWidth || !containerHeight) return undefined;
    const horizontalPadding = containerWidth < 640 ? 16 : containerWidth < 1024 ? 32 : 48;
    const verticalPadding = containerHeight < 640 ? 24 : 48;
    const availableWidth = Math.max(MOBILE_MIN_PAGE_WIDTH, containerWidth - horizontalPadding);
    const availableHeight = Math.max(MOBILE_MIN_PAGE_WIDTH, containerHeight - verticalPadding);
    const ratio = pageAspectRatios.get(pageNumber) ?? fallbackAspectRatio;
    let w = availableWidth;
    let h = w * ratio;
    if (h > availableHeight) {
      h = availableHeight;
      w = h / ratio;
    }
    return Math.round(w);
  }, [viewMode, containerWidth, containerHeight, pageAspectRatios, fallbackAspectRatio, pageNumber]);

  const renderedSlideWidth = slideBaseWidth ? Math.round(slideBaseWidth * scale) : undefined;
  const renderedSlideHeight = renderedSlideWidth
    ? Math.round(renderedSlideWidth * (pageAspectRatios.get(pageNumber) ?? fallbackAspectRatio))
    : undefined;

  const applyZoomLayer = useCallback((nextScale: number) => {
    const layer = zoomLayerRef.current;
    if (!layer) return;
    layer.style.setProperty("zoom", String(nextScale / scale));
  }, [scale]);

  const syncZoomDisplay = useCallback((next: number, immediate = false) => {
    if (zoomStateTimerRef.current) window.clearTimeout(zoomStateTimerRef.current);
    if (immediate) {
      setVisualScale(next);
      return;
    }
    zoomStateTimerRef.current = window.setTimeout(() => {
      setVisualScale(visualScaleRef.current);
    }, 80);
  }, []);

  // Measure reader width so the default view can fit the page exactly like
  // browser-native PDF readers do. Zoom multiplies this fitted width.
  useEffect(() => {
    if (!pageHostRef.current) return;
    const el = pageHostRef.current;
    const measure = () => {
      setContainerWidth(el.clientWidth);
      setContainerHeight(el.clientHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep `pageNumber` in sync with the most visible rendered page while the
  // user scrolls continuous pages. This drives the toolbar counter and the
  // mobile pill without forcing a page jump.
  useEffect(() => {
    const root = pageHostRef.current;
    if (!root || numPages <= 0) return;

    const visible = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        if (isZoomingRef.current || isPageJumpingRef.current) return;
        for (const entry of entries) {
          const raw = (entry.target as HTMLElement).dataset.pageNumber;
          if (!raw) continue;
          const page = Number(raw);
          if (entry.isIntersecting) visible.set(page, entry.intersectionRatio);
          else visible.delete(page);
        }
        if (visible.size === 0) return;
        const [best] = Array.from(visible.entries()).sort((a, b) => b[1] - a[1])[0];
        setPageNumber(best);
      },
      {
        root,
        threshold: [0.1, 0.25, 0.4, 0.55, 0.7, 0.85],
      },
    );

    pageRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [numPages, renderedPageWidth]);

  const zoomAtPoint = useCallback((nextScale: number, clientX?: number, clientY?: number, immediate = false) => {
    const el = pageHostRef.current;
    const zoomLayer = zoomLayerRef.current;
    const current = visualScaleRef.current;
    const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, +nextScale.toFixed(3)));
    if (!el || !zoomLayer || Math.abs(next - current) < 0.001) return;

    const rect = el.getBoundingClientRect();
    const offsetX = clientX == null ? el.clientWidth / 2 : clientX - rect.left;
    const offsetY = clientY == null ? el.clientHeight / 2 : clientY - rect.top;
    const beforeLeft = el.scrollLeft;
    const beforeTop = el.scrollTop;
    const ratio = next / current;

    visualScaleRef.current = next;
    isZoomingRef.current = true;
    if (zoomSettleTimerRef.current) window.clearTimeout(zoomSettleTimerRef.current);
    zoomSettleTimerRef.current = window.setTimeout(() => {
      isZoomingRef.current = false;
      syncPageFromScroll();
    }, 180);
    if (zoomRafRef.current) window.cancelAnimationFrame(zoomRafRef.current);
    zoomRafRef.current = window.requestAnimationFrame(() => {
      if (!pageHostRef.current || !zoomLayerRef.current) return;
      applyZoomLayer(next);
      pageHostRef.current.scrollLeft = (beforeLeft + offsetX) * ratio - offsetX;
      pageHostRef.current.scrollTop = (beforeTop + offsetY) * ratio - offsetY;
      syncZoomDisplay(next, immediate);
    });
  }, [applyZoomLayer, syncPageFromScroll, syncZoomDisplay]);

  // Gesture handling:
  // - laptop trackpad pinch / Ctrl+wheel / Cmd+wheel zooms at cursor
  // - mobile two-finger pinch zooms at gesture midpoint
  // - mobile one-finger pan and long-press selection stay browser-native
  // This keeps the invisible PDF text layer selectable on phones while the
  // custom handler remains responsible only for an actual two-finger pinch.
  useEffect(() => {
    const el = pageHostRef.current;
    if (!el) return;

    const pointers = new Map<number, { x: number; y: number }>();
    let pinchStart: { dist: number; scale: number; centerX: number; centerY: number } | null = null;
    let lastTapAt = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
      Math.hypot(a.x - b.x, a.y - b.y);

    const center = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
    });

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rawFactor = Math.exp(-e.deltaY * 0.0026);
      const factor = Math.max(0.925, Math.min(1.075, rawFactor));
      zoomAtPoint(visualScaleRef.current * factor, e.clientX, e.clientY);
    };

    // Safari/iOS still emits legacy GestureEvents for pinch. Preventing
    // them on the PDF pane stops the browser from zooming the entire page
    // while our pointer handlers zoom only the rendered PDF.
    const preventPageGesture = (e: Event) => {
      e.preventDefault();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        e.preventDefault();
        const [p1, p2] = Array.from(pointers.values());
        const c = center(p1, p2);
        pinchStart = { dist: dist(p1, p2), scale: visualScaleRef.current, centerX: c.x, centerY: c.y };
      } else if (pointers.size === 1) {
        // Detect double-tap (within 320ms, within 32px of last tap).
        const now = performance.now();
        if (now - lastTapAt < 320 && Math.hypot(e.clientX - lastTapX, e.clientY - lastTapY) < 32) {
          // Toggle between fit-width and a readable punch-in.
          zoomAtPoint(visualScaleRef.current > 1.35 ? DEFAULT_SCALE : 1.75, e.clientX, e.clientY);
          lastTapAt = 0; // consume
        } else {
          lastTapAt = now;
          lastTapX = e.clientX;
          lastTapY = e.clientY;
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2 && pinchStart) {
        const [p1, p2] = Array.from(pointers.values());
        const d = dist(p1, p2);
        if (d > 0 && pinchStart.dist > 0) {
          const ratio = d / pinchStart.dist;
          zoomAtPoint(pinchStart.scale * ratio, pinchStart.centerX, pinchStart.centerY);
          e.preventDefault();
        }
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchStart = null;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("gesturestart", preventPageGesture, { passive: false });
    el.addEventListener("gesturechange", preventPageGesture, { passive: false });
    el.addEventListener("gestureend", preventPageGesture, { passive: false });
    el.addEventListener("pointerdown",   onPointerDown);
    el.addEventListener("pointermove",   onPointerMove,   { passive: false });
    el.addEventListener("pointerup",     onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", preventPageGesture);
      el.removeEventListener("gesturechange", preventPageGesture);
      el.removeEventListener("gestureend", preventPageGesture);
      el.removeEventListener("pointerdown",   onPointerDown);
      el.removeEventListener("pointermove",   onPointerMove);
      el.removeEventListener("pointerup",     onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [zoomAtPoint]);

  // Reset to page 1 when the URL changes (chapter switch).
  useEffect(() => {
    setPageNumber(1);
    setScale(DEFAULT_SCALE);
    setVisualScale(DEFAULT_SCALE);
    visualScaleRef.current = DEFAULT_SCALE;
    isZoomingRef.current = false;
    isPageJumpingRef.current = false;
    setPageAspectRatios(new Map());
    applyZoomLayer(DEFAULT_SCALE);
  }, [applyZoomLayer, url]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  }, []);

  // Report the reading position outward. Both `pageNumber` (driven by the
  // IntersectionObserver in scroll mode, by the pager in slide mode) and
  // `numPages` are already maintained above; this just surfaces them.
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  useEffect(() => {
    if (numPages > 0) onPageChangeRef.current?.(pageNumber, numPages);
  }, [pageNumber, numPages]);

  const onPageLoadSuccess = useCallback((pageNumber: number, page: {
    width?: number;
    height?: number;
    originalWidth?: number;
    originalHeight?: number;
  }) => {
    const width = page.originalWidth ?? page.width;
    const height = page.originalHeight ?? page.height;
    if (!width || !height) return;
    const ratio = height / width;
    if (!Number.isFinite(ratio)) return;
    setPageAspectRatios((prev) => {
      if (Math.abs((prev.get(pageNumber) ?? -1) - ratio) < 0.005) return prev;
      const next = new Map(prev);
      next.set(pageNumber, ratio);
      return next;
    });
  }, []);

  const goToPage = useCallback((page: number, opts?: { close?: boolean }) => {
    const next = Math.max(1, Math.min(numPages || page, page));
    isPageJumpingRef.current = true;
    if (pageJumpTimerRef.current) window.clearTimeout(pageJumpTimerRef.current);
    setPageNumber(next);
    pageRefs.current.get(next)?.scrollIntoView({ block: "start", behavior: "smooth" });
    pageJumpTimerRef.current = window.setTimeout(() => {
      isPageJumpingRef.current = false;
      syncPageFromScroll();
    }, 420);
    if (opts?.close) setSidebarOpen(false);
  }, [numPages, syncPageFromScroll]);

  // Keyboard nav: ArrowLeft / ArrowRight, ignored if focus is in an input.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      const isZoomShortcut = e.ctrlKey || e.metaKey;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPage(pageNumber - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToPage(pageNumber + 1);
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomAtPoint(visualScaleRef.current + SCALE_STEP, undefined, undefined, true);
      } else if (e.key === "-") {
        e.preventDefault();
        zoomAtPoint(visualScaleRef.current - SCALE_STEP, undefined, undefined, true);
      } else if (isZoomShortcut && e.key === "0") {
        e.preventDefault();
        zoomAtPoint(DEFAULT_SCALE, undefined, undefined, true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goToPage, pageNumber, zoomAtPoint]);

  const setZoom = (next: number) => {
    zoomAtPoint(next, undefined, undefined, true);
  };

  const resetFitWidth = () => zoomAtPoint(DEFAULT_SCALE, undefined, undefined, true);

  return (
    <div className={cn("flex flex-col h-[calc(100dvh-8.75rem)] min-h-[520px]", className)}>
      {/* Toolbar ------------------------------------------------------- */}
      <div className="z-20 flex-shrink-0 border-b border-border bg-surface/95 backdrop-blur-md">
        <div
          className={cn(
            "px-3 sm:px-4 lg:px-6 min-h-14 py-2 flex items-center gap-2 sm:gap-3",
            showStudyControls ? "w-full" : "mx-auto max-w-7xl",
          )}
        >
          {showStudyControls ? (
            <Button
              variant="ghost"
              size="icon"
              className="-ml-1.5 flex-shrink-0"
              aria-label="Back"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          ) : null}

          {showStudyControls ? (
            <div className="hidden sm:flex flex-shrink-0 items-center pr-2">
              <Brand to="/dashboard" />
            </div>
          ) : null}

          {sections && sections.length > 0 ? (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden -ml-1.5"
              aria-label="Sections"
              onClick={() => setSidebarOpen((v) => !v)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          ) : null}

          <div className="flex min-w-0 flex-1 items-center gap-2">
            {ModeIcon ? (
              <span
                className={cn(
                  "hidden sm:grid h-8 w-8 flex-shrink-0 place-items-center rounded-md",
                  modeAccent ?? "bg-brand-soft text-brand",
                )}
              >
                <ModeIcon className="h-4 w-4" />
              </span>
            ) : null}
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate font-display text-sm sm:text-base font-semibold tracking-tight text-text">
                  {title ?? "PDF viewer"}
                </p>
                {subtitle ? (
                  <span className="hidden md:inline text-xs text-text-muted truncate">
                    {subtitle}
                  </span>
                ) : null}
              </div>
              {helperText ? (
                <p className="hidden lg:block truncate text-xs text-text-muted">
                  {helperText}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Zoom out"
              disabled={visualScale <= MIN_SCALE}
              onClick={() => setZoom(visualScaleRef.current - SCALE_STEP)}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="text-xs text-text-muted tabular-nums w-12 text-center">
              {Math.round(visualScale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Zoom in"
              disabled={visualScale >= MAX_SCALE}
              onClick={() => setZoom(visualScaleRef.current + SCALE_STEP)}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={viewMode === "slide" ? "Fit to screen" : "Fit to width"}
              disabled={visualScale === DEFAULT_SCALE}
              onClick={resetFitWidth}
              className="hidden sm:inline-flex"
            >
              <Maximize className="h-4 w-4" />
            </Button>
          </div>

          <div className="hidden sm:flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Previous page"
              disabled={pageNumber <= 1}
              onClick={() => goToPage(pageNumber - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-text-muted tabular-nums">
              <span className="text-text font-medium">{pageNumber}</span>
              <span className="mx-1">/</span>
              <span>{numPages || "..."}</span>
            </span>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Next page"
              disabled={pageNumber >= numPages}
              onClick={() => goToPage(pageNumber + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {toolbarSlot}

          {showStudyControls ? <ThemeToggle /> : null}
        </div>
      </div>

      {/* Body --------------------------------------------------------- */}
      <div className="flex flex-1 min-h-0 relative overflow-hidden">
        {/* Sidebar (desktop) */}
        {sections && sections.length > 0 ? (
          <aside className="hidden lg:block w-72 flex-shrink-0 border-r border-border bg-surface dark:bg-bg overflow-y-auto overscroll-contain py-4">
            <SectionsList sections={sections} onSelect={(s) => onSectionClick?.(s)} />
          </aside>
        ) : null}

        {/* Sidebar (mobile sheet) */}
        <AnimatePresence>
          {sidebarOpen && sections && sections.length > 0 ? (
            <>
              <motion.div
                key="backdrop"
                className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setSidebarOpen(false)}
              />
              <motion.aside
                key="sheet"
                className="lg:hidden fixed inset-y-0 left-0 z-40 w-72 bg-surface border-r border-border overflow-y-auto py-4"
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", stiffness: 380, damping: 36 }}
              >
                <div className="flex items-center justify-between px-4 pb-3 border-b border-border">
                  <span className="text-xs uppercase tracking-[0.16em] text-text-muted">Sections</span>
                  <button
                    type="button"
                    className="p-1 rounded-md text-text-muted hover:text-text hover:bg-surface-2"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <SectionsList
                  sections={sections}
                  onSelect={(s) => {
                    onSectionClick?.(s);
                    setSidebarOpen(false);
                  }}
                />
              </motion.aside>
            </>
          ) : null}
        </AnimatePresence>

        {/* PDF host */}
        <div
          ref={pageHostRef}
          className="flex-1 min-w-0 overflow-auto overscroll-contain bg-surface-2 dark:bg-bg px-2 sm:px-4 lg:px-6 py-3 sm:py-6"
          // Native one-finger panning preserves long-press text selection;
          // excluding pinch-zoom leaves two-finger scaling to our handler.
          style={{ touchAction: "pan-x pan-y" }}
        >
          <div ref={zoomLayerRef} style={{ transformOrigin: "top center" }}>
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<PDFLoading />}
              error={<PDFError />}
              className={
                viewMode === "slide"
                  ? "w-full min-h-full flex items-center justify-center"
                  : "mx-auto flex w-max min-w-full flex-col items-center"
              }
            >
              {numPages > 0 ? (
                viewMode === "slide" ? (
                  <Page
                    pageNumber={pageNumber}
                    width={renderedSlideWidth}
                    renderTextLayer
                    renderAnnotationLayer={false}
                    onLoadSuccess={(info) => onPageLoadSuccess(pageNumber, info)}
                    className="shadow-[0_4px_20px_rgb(15_23_42_/_0.08)] rounded-md overflow-hidden bg-white"
                    loading={<PageSkeleton width={renderedSlideWidth} height={renderedSlideHeight} />}
                  />
                ) : (
                  Array.from({ length: numPages }, (_, i) => {
                    const page = i + 1;
                    const shouldRenderPage =
                      Math.abs(page - pageNumber) <= PAGE_RENDER_RADIUS ||
                      page <= 2 ||
                      page > numPages - 2;
                    return (
                      <div
                        key={page}
                        ref={(node) => {
                          if (node) pageRefs.current.set(page, node);
                          else pageRefs.current.delete(page);
                        }}
                        data-page-number={page}
                        className="mb-4 scroll-mt-4"
                        style={
                          shouldRenderPage
                            ? undefined
                            : {
                                width: renderedPageWidth ?? 640,
                                height: estimatedHeightForPage(page) ?? 900,
                              }
                        }
                      >
                        {shouldRenderPage ? (
                          <Page
                            pageNumber={page}
                            width={renderedPageWidth}
                            renderTextLayer
                            renderAnnotationLayer={false}
                            onLoadSuccess={(info) => onPageLoadSuccess(page, info)}
                            className="shadow-[0_4px_20px_rgb(15_23_42_/_0.08)] rounded-md overflow-hidden bg-white"
                            loading={<PageSkeleton width={renderedPageWidth} />}
                          />
                        ) : null}
                      </div>
                    );
                  })
                )
              ) : null}
            </Document>
          </div>
        </div>
      </div>

      {/* Mobile page nav (bottom-anchored bar) */}
      <div
        className={cn(
          "sm:hidden fixed left-1/2 -translate-x-1/2 z-30",
          "bottom-[calc(1rem+env(safe-area-inset-bottom))]",
          "flex items-center gap-2 px-2 py-1.5 rounded-full",
          "border border-border bg-surface/95 backdrop-blur-md shadow-md",
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          aria-label="Previous page"
          disabled={pageNumber <= 1}
          onClick={() => goToPage(pageNumber - 1)}
          className="h-9 w-9 rounded-full"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs tabular-nums px-1.5 min-w-[3.5rem] text-center">
          <span className="font-medium">{pageNumber}</span>
          <span className="text-text-muted"> / {numPages || "..."}</span>
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Next page"
          disabled={pageNumber >= numPages}
          onClick={() => goToPage(pageNumber + 1)}
          className="h-9 w-9 rounded-full"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function SectionsList({
  sections,
  onSelect,
}: {
  sections: Section[];
  onSelect: (s: Section) => void;
}) {
  return (
    <nav className="px-2">
      <ol className="flex flex-col">
        {sections.map((s) => {
          const display = splitSectionTitle(s.title);
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s)}
                className={cn(
                  "w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-lg",
                  "text-sm text-text-muted hover:text-text hover:bg-surface-2",
                  "transition-colors",
                )}
              >
                <span className="font-mono text-[11px] tabular-nums text-text-muted pt-0.5 w-12 flex-shrink-0">
                  {display.number}
                </span>
                <span className="leading-snug">{display.title}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function splitSectionTitle(title: string): { number: string; title: string } {
  const clean = title.replace(/\.txt$/i, "").trim();
  const match = clean.match(/^(\d+(?:\.\d+)*)(?:\s+|\.\s*)(.+)$/);
  if (!match) {
    return { number: "", title: clean };
  }
  return { number: match[1], title: match[2].trim() };
}

function PDFLoading() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-text-muted">
      <Loader2 className="h-5 w-5 animate-spin text-brand" />
      <span className="text-sm">Loading content...</span>
    </div>
  );
}

function PDFError() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-2 max-w-md text-center">
      <p className="text-sm font-medium">Couldn't load this content.</p>
      <p className="text-xs text-text-muted">
        The file might still be uploading. Try again in a moment.
      </p>
    </div>
  );
}

function PageSkeleton({ width, height }: { width?: number; height?: number }) {
  return (
    <Skeleton
      className={height ? "rounded-md" : "h-[70vh] max-h-[960px] min-h-[420px] rounded-md"}
      style={{ width: width ?? 640, ...(height ? { height } : {}) }}
    />
  );
}
